import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { birthdayService } from '@/services/birthday.service';
import type { Birthday, CreateBirthdayDto, BirthdayFilter } from '@/types/birthday.types';
import { ApiError } from '@/services/api';

// Birthday state interface
interface BirthdayState {
  birthdays: Birthday[];
  upcomingBirthdays: Birthday[];
  birthdaysThisMonth: Birthday[];
  birthdaysThisWeek: Birthday[];
  myBirthday: Birthday | null;
  filter: BirthdayFilter;
  isLoading: boolean;
  isLoadingMy: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  error: string | null;
}

// Initial state
const initialState: BirthdayState = {
  birthdays: [],
  upcomingBirthdays: [],
  birthdaysThisMonth: [],
  birthdaysThisWeek: [],
  myBirthday: null,
  filter: 'all',
  isLoading: false,
  isLoadingMy: false,
  isSaving: false,
  isDeleting: false,
  error: null,
};

// Async thunks

// Fetch all birthdays
export const fetchAllBirthdays = createAsyncThunk<
  Birthday[],
  void,
  { rejectValue: string }
>('birthdays/fetchAll', async (_, { rejectWithValue }) => {
  try {
    return await birthdayService.getAllBirthdays();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Geburtstage');
  }
});

// Fetch upcoming birthdays
export const fetchUpcomingBirthdays = createAsyncThunk<
  Birthday[],
  number | undefined,
  { rejectValue: string }
>('birthdays/fetchUpcoming', async (days = 30, { rejectWithValue }) => {
  try {
    return await birthdayService.getUpcomingBirthdays(days);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der bevorstehenden Geburtstage');
  }
});

// Fetch birthdays this month
export const fetchBirthdaysThisMonth = createAsyncThunk<
  Birthday[],
  void,
  { rejectValue: string }
>('birthdays/fetchThisMonth', async (_, { rejectWithValue }) => {
  try {
    return await birthdayService.getBirthdaysThisMonth();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Geburtstage diesen Monat');
  }
});

// Fetch birthdays this week
export const fetchBirthdaysThisWeek = createAsyncThunk<
  Birthday[],
  void,
  { rejectValue: string }
>('birthdays/fetchThisWeek', async (_, { rejectWithValue }) => {
  try {
    return await birthdayService.getBirthdaysThisWeek();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Geburtstage diese Woche');
  }
});

// Fetch my birthday
export const fetchMyBirthday = createAsyncThunk<
  Birthday | null,
  void,
  { rejectValue: string }
>('birthdays/fetchMy', async (_, { rejectWithValue }) => {
  try {
    return await birthdayService.getMyBirthday();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden deines Geburtstags');
  }
});

// Create or update birthday
export const saveMyBirthday = createAsyncThunk<
  Birthday,
  CreateBirthdayDto,
  { rejectValue: string }
>('birthdays/save', async (data, { rejectWithValue }) => {
  try {
    return await birthdayService.createOrUpdateBirthday(data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Speichern des Geburtstags');
  }
});

// Delete birthday
export const deleteMyBirthday = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>('birthdays/delete', async (_, { rejectWithValue }) => {
  try {
    await birthdayService.deleteBirthday();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Loeschen des Geburtstags');
  }
});

// Birthday slice
const birthdaySlice = createSlice({
  name: 'birthdays',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilter: (state, action: PayloadAction<BirthdayFilter>) => {
      state.filter = action.payload;
    },
    resetBirthdays: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch all birthdays
    builder
      .addCase(fetchAllBirthdays.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllBirthdays.fulfilled, (state, action) => {
        state.isLoading = false;
        state.birthdays = action.payload;
      })
      .addCase(fetchAllBirthdays.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch upcoming birthdays
    builder
      .addCase(fetchUpcomingBirthdays.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUpcomingBirthdays.fulfilled, (state, action) => {
        state.isLoading = false;
        state.upcomingBirthdays = action.payload;
      })
      .addCase(fetchUpcomingBirthdays.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch birthdays this month
    builder
      .addCase(fetchBirthdaysThisMonth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBirthdaysThisMonth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.birthdaysThisMonth = action.payload;
      })
      .addCase(fetchBirthdaysThisMonth.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch birthdays this week
    builder
      .addCase(fetchBirthdaysThisWeek.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBirthdaysThisWeek.fulfilled, (state, action) => {
        state.isLoading = false;
        state.birthdaysThisWeek = action.payload;
      })
      .addCase(fetchBirthdaysThisWeek.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch my birthday
    builder
      .addCase(fetchMyBirthday.pending, (state) => {
        state.isLoadingMy = true;
        state.error = null;
      })
      .addCase(fetchMyBirthday.fulfilled, (state, action) => {
        state.isLoadingMy = false;
        state.myBirthday = action.payload;
      })
      .addCase(fetchMyBirthday.rejected, (state, action) => {
        state.isLoadingMy = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Save birthday
    builder
      .addCase(saveMyBirthday.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveMyBirthday.fulfilled, (state, action) => {
        state.isSaving = false;
        state.myBirthday = action.payload;
        // Update in birthdays list if exists
        const index = state.birthdays.findIndex((b) => b.id === action.payload.id);
        if (index !== -1) {
          state.birthdays[index] = action.payload;
        } else {
          state.birthdays.push(action.payload);
        }
      })
      .addCase(saveMyBirthday.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload ?? 'Fehler beim Speichern';
      });

    // Delete birthday
    builder
      .addCase(deleteMyBirthday.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteMyBirthday.fulfilled, (state) => {
        state.isDeleting = false;
        if (state.myBirthday) {
          state.birthdays = state.birthdays.filter((b) => b.id !== state.myBirthday?.id);
        }
        state.myBirthday = null;
      })
      .addCase(deleteMyBirthday.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? 'Fehler beim Loeschen';
      });
  },
});

// Export actions
export const { clearError, setFilter, resetBirthdays } = birthdaySlice.actions;

// Selectors
export const selectBirthdays = (state: { birthdays: BirthdayState }) => state.birthdays.birthdays;
export const selectUpcomingBirthdays = (state: { birthdays: BirthdayState }) => state.birthdays.upcomingBirthdays;
export const selectBirthdaysThisMonth = (state: { birthdays: BirthdayState }) => state.birthdays.birthdaysThisMonth;
export const selectBirthdaysThisWeek = (state: { birthdays: BirthdayState }) => state.birthdays.birthdaysThisWeek;
export const selectMyBirthday = (state: { birthdays: BirthdayState }) => state.birthdays.myBirthday;
export const selectBirthdayFilter = (state: { birthdays: BirthdayState }) => state.birthdays.filter;
export const selectBirthdayLoading = (state: { birthdays: BirthdayState }) => ({
  isLoading: state.birthdays.isLoading,
  isLoadingMy: state.birthdays.isLoadingMy,
  isSaving: state.birthdays.isSaving,
  isDeleting: state.birthdays.isDeleting,
});
export const selectBirthdayError = (state: { birthdays: BirthdayState }) => state.birthdays.error;

// Export reducer
export default birthdaySlice.reducer;
